import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CustomerDetail } from '@/lib/api/customers';
import { cn } from "@/lib/utils";
import { Mail, MapPin, Phone, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CustomerEditDialog } from '@/components/form/customer/customer-edit-dialog';

interface CustomerDetailsProps {
  customer: CustomerDetail;
  className?: string;
}

function CustomerDetails({ customer, className }: CustomerDetailsProps) {
  const { t } = useTranslation('customers');

  return (
    <Card className={cn('h-fit', className)}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('customerInformation')}</CardTitle>
        <CustomerEditDialog customer={customer} />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4">
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('name')}</p>
            <div className="flex items-center">
              <User className="text-muted-foreground mr-2 h-4 w-4" />
              <p>
                {customer.firstName || 'N/A'} {customer.lastName || 'N/A'}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('email')}</p>
            <div className="flex items-center">
              <Mail className="text-muted-foreground mr-2 h-4 w-4" />
              <p>{customer.email || 'N/A'}</p>
            </div>
          </div>

          {customer.billingAddress?.phone && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm font-medium">{t('phone')}</p>
              <div className="flex items-center">
                <Phone className="text-muted-foreground mr-2 h-4 w-4" />
                <p>{customer.billingAddress.phone}</p>
              </div>
            </div>
          )}
        </div>

        <Separator className="my-6" />

        <div className="grid grid-cols-1 gap-6">
          {customer.billingAddress && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('billingAddress')}
              </h3>
              <div className="space-y-1">
                <p>
                  {customer.billingAddress.firstName || 'N/A'} {customer.billingAddress.lastName || 'N/A'}
                </p>
                {customer.billingAddress.company && <p>{customer.billingAddress.company}</p>}
                <p>{customer.billingAddress.address1 || 'N/A'}</p>
                <p>
                  {customer.billingAddress.city || 'N/A'}, {customer.billingAddress.state || 'N/A'}{' '}
                  {customer.billingAddress.postal || 'N/A'}
                </p>
                <p>{customer.billingAddress.country || 'N/A'}</p>
              </div>
            </div>
          )}

          {customer.shippingAddress && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('shippingAddress')}
              </h3>
              <div className="space-y-1">
                <p>
                  {customer.shippingAddress.firstName || 'N/A'} {customer.shippingAddress.lastName || 'N/A'}
                </p>
                {customer.shippingAddress.company && <p>{customer.shippingAddress.company}</p>}
                <p>{customer.shippingAddress.address1 || 'N/A'}</p>
                <p>
                  {customer.shippingAddress.city || 'N/A'}, {customer.shippingAddress.state || 'N/A'}{' '}
                  {customer.shippingAddress.postal || 'N/A'}
                </p>
                <p>{customer.shippingAddress.country || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default CustomerDetails;
